import React from 'react';
import { Persona, PersonaSize, Stack } from '@fluentui/react';
import clsx from 'clsx';
import { IStyledComponent } from 'common/Common.types';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { Tag } from 'common/components/Tag';
import { Heading3 } from 'common/components/Text';
import { useLocalizedStrings } from 'i18n';
import { FamilyMember } from 'mcsclient/models';
import { useAppSelector } from 'store/hooks';
import { getFamilyMemberNotificationSetting } from 'store/notifications/notificationSelectors';
import { useNotificationsDispatch } from 'store/notifications/useNotificationSettings';

import { stackTokens, useNotificationSettingsStyles } from '../NotificationSettings.styles';
import { NotificationSettingsSummary } from '../NotificationSettingsSummary';

interface IFamilyMemberViewProps extends IStyledComponent {
  member: FamilyMember;
  onDrillDown?: () => void;
}

export const FamilyMemberView = (props: IFamilyMemberViewProps) => {
  const { member, onDrillDown, className } = props;
  const { t } = useLocalizedStrings();
  const familyMemberSetting = useAppSelector((state) => getFamilyMemberNotificationSetting(state.notifications, member.puid!));
  const { updateFamilyMemberNotificationSettings } = useNotificationsDispatch();
  const classNames = useNotificationSettingsStyles();
  const { isMobileView } = usePanelMobileView();

  const { firstName, displayName, userPrincipalName, role } = member;
  const name = firstName || displayName || userPrincipalName || '';

  const confirmSubtext = t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.TurnOffConfirm.Subtext', { name });
  const confirmTitle = t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.TurnOffConfirm.Title', { name });

  // use Heading3 for use in heading keyboard shortcuts
  const onRenderName = React.useCallback(
    () => (
      <Heading3 variant="Body1" className={classNames.personaName}>
        {name}
      </Heading3>
    ),
    [classNames.personaName, name]
  );

  const onToggle = React.useCallback(
    (isEnabled: boolean) => {
      updateFamilyMemberNotificationSettings({
        memberUserId: member.puid!,
        isEnabled,
      });
    },
    [member.puid, updateFamilyMemberNotificationSettings]
  );

  const enabledTopicShortNames = React.useMemo(() => {
    if (onDrillDown) {
      if (familyMemberSetting?.notificationSetting.areAllTopicsEnabled) {
        return [t('OfficeShellPanel.NotificationSettings.Sections.AllNotifications')];
      }
      return familyMemberSetting?.notificationSetting.enabledTopicShortNames;
    }
    return undefined;
  }, [
    familyMemberSetting?.notificationSetting.areAllTopicsEnabled,
    familyMemberSetting?.notificationSetting.enabledTopicShortNames,
    onDrillDown,
    t,
  ]);

  const notificationsEnabled = familyMemberSetting?.notificationSetting.isEnabled ?? false;
  const toggleNotificationLabel = notificationsEnabled
    ? t('OfficeShellPanel.NotificationSettings.Sections.On')
    : t('OfficeShellPanel.NotificationSettings.Sections.Off');

  const toggleAriaLabel = notificationsEnabled
    ? t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.ToggleAriaLabelOn', { name })
    : t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.ToggleAriaLabelOff', { name });

  const onRenderMember = React.useCallback(
    () => (
      <Stack tokens={stackTokens.gapXS}>
        <Persona
          size={PersonaSize.size40}
          onRenderPrimaryText={onRenderName}
          secondaryText={
            role === 'Admin'
              ? t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.Role.Admin')
              : t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.Role.Member')
          }
          imageUrl={member.photoUrl}
        />
        {notificationsEnabled && !!enabledTopicShortNames?.length && (
          <Stack horizontal wrap tokens={stackTokens.gapXXS}>
            {enabledTopicShortNames.map((topicShortName) => (
              <Tag key={topicShortName} state="Grey" text={topicShortName} />
            ))}
          </Stack>
        )}
      </Stack>
    ),
    [enabledTopicShortNames, notificationsEnabled, onRenderName, role, t, member.photoUrl]
  );

  return (
    <NotificationSettingsSummary
      className={clsx(
        classNames.familyMemberView,
        onDrillDown && !isMobileView ? classNames.elevatedWithRadius : undefined,
        className
      )}
      onRenderTitle={onRenderMember}
      notificationType="FamilyMember"
      telemetryProps={{ familyMemberRole: role }}
      toggleNotificationLabel={toggleNotificationLabel}
      toggleAriaLabel={toggleAriaLabel}
      notificationsEnabled={notificationsEnabled}
      isToggleable={familyMemberSetting?.notificationSetting.isToggleable}
      onToggleNotification={onToggle}
      onDrillDown={onDrillDown}
      drillDownButtonAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Family.Member.DrillDownButtonAriaLabel', { name })}
      turnOffConfirmStrings={{
        title: confirmTitle,
        subText: confirmSubtext,
      }}
    />
  );
};
